import { useState } from 'react';
import { Calculator, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { AgenteIaContextoSeletor, type AgenteIaContexto } from '@/components/agentes-ia/AgenteIaContextoSeletor';
import { useAgenteIaJob } from '@/hooks/useAgenteIaJob';
import { usePrecificacaoResultado } from '@/hooks/useCentralPlanilhas';
import type { ItemBruto } from '@/types/agentesIa';

function EmptyState({ message }: { message: string }) {
  return <p className="text-sm text-ds-text-secondary italic text-center py-8">{message}</p>;
}

const DEFAULT_BDI = 12.5;
const DEFAULT_ENCARGOS = 18.0;
const DEFAULT_MARGEM = 10.0;

function novoItem(): ItemBruto {
  return { codigo: '', descricao: '', unidade: '', quantidade: 0, custo_unitario: 0 };
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function validar(itens: ItemBruto[], bdi: number, encargos: number, margem: number): string | null {
  if (itens.length === 0) return 'Adicione ao menos um item.';
  for (const item of itens) {
    if (!item.codigo || !item.descricao || !item.unidade) {
      return 'Preencha código, descrição e unidade de todos os itens.';
    }
    if (!(item.quantidade > 0)) return 'Quantidade deve ser maior que zero em todos os itens.';
    if (!(item.custo_unitario >= 0)) return 'Custo unitário não pode ser negativo.';
  }
  for (const [valor, label] of [[bdi, 'BDI'], [encargos, 'Encargos'], [margem, 'Margem']] as const) {
    if (valor < 0 || valor > 100) return `${label} deve estar entre 0 e 100.`;
  }
  return null;
}

function CentralPlanilhas() {
  const [contexto, setContexto] = useState<AgenteIaContexto | null>(null);
  const [itens, setItens] = useState<ItemBruto[]>([novoItem()]);
  const [bdi, setBdi] = useState(DEFAULT_BDI);
  const [encargos, setEncargos] = useState(DEFAULT_ENCARGOS);
  const [margem, setMargem] = useState(DEFAULT_MARGEM);

  const { enqueue, isEnqueuing, jobStatus, reset } = useAgenteIaJob('central-planilhas', contexto?.empresaId);
  const { data: resultado, isLoading: resultadoLoading } = usePrecificacaoResultado(
    contexto?.empresaId,
    contexto?.licitacaoId,
    jobStatus?.status
  );

  const isRunning = Boolean(jobStatus && jobStatus.status !== 'completed' && jobStatus.status !== 'failed');
  const erroValidacao = validar(itens, bdi, encargos, margem);

  const handleCalcular = async () => {
    if (!contexto || erroValidacao) return;
    reset();
    await enqueue({
      licitacaoId: contexto.licitacaoId,
      documentoId: contexto.documentoId,
      dadosBrutos: itens,
      parametros: { bdi, encargos, margem },
    });
  };

  const atualizarItem = (index: number, patch: Partial<ItemBruto>) => {
    setItens((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const removerItem = (index: number) => {
    setItens((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ds-text-primary flex items-center gap-2">
          <Calculator className="h-6 w-6 text-ds-success" />
          Central de Planilhas
        </h1>
        <p className="text-ds-text-secondary mt-1">
          Informe os itens e custos brutos e obtenha a planilha de precificação calculada.
        </p>
      </div>

      <AgenteIaContextoSeletor onContextoCompleto={setContexto} />

      <Card>
        <CardHeader>
          <CardTitle>Itens e parâmetros</CardTitle>
          <CardDescription>Custos brutos por item e percentuais de BDI, encargos e margem.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            {itens.map((item, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr_1fr_1fr_auto] gap-3 items-end">
                <Input
                  label={index === 0 ? 'Código' : undefined}
                  value={item.codigo}
                  onChange={(e) => atualizarItem(index, { codigo: e.target.value })}
                />
                <Input
                  label={index === 0 ? 'Descrição' : undefined}
                  value={item.descricao}
                  onChange={(e) => atualizarItem(index, { descricao: e.target.value })}
                />
                <Input
                  label={index === 0 ? 'Unidade' : undefined}
                  value={item.unidade}
                  onChange={(e) => atualizarItem(index, { unidade: e.target.value })}
                />
                <Input
                  label={index === 0 ? 'Quantidade' : undefined}
                  type="number"
                  min={0}
                  step="0.01"
                  value={item.quantidade}
                  onChange={(e) => atualizarItem(index, { quantidade: Number(e.target.value) })}
                />
                <Input
                  label={index === 0 ? 'Custo unitário' : undefined}
                  type="number"
                  min={0}
                  step="0.01"
                  value={item.custo_unitario}
                  onChange={(e) => atualizarItem(index, { custo_unitario: Number(e.target.value) })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removerItem(index)}
                  disabled={itens.length === 1}
                >
                  <Trash2 className="h-4 w-4 text-ds-danger" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setItens((prev) => [...prev, novoItem()])}
            >
              <Plus className="h-4 w-4 mr-1" />
              Adicionar item
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-ds-border">
            <Input
              label="BDI (%)"
              type="number"
              min={0}
              max={100}
              step="0.1"
              value={bdi}
              onChange={(e) => setBdi(Number(e.target.value))}
            />
            <Input
              label="Encargos (%)"
              type="number"
              min={0}
              max={100}
              step="0.1"
              value={encargos}
              onChange={(e) => setEncargos(Number(e.target.value))}
            />
            <Input
              label="Margem (%)"
              type="number"
              min={0}
              max={100}
              step="0.1"
              value={margem}
              onChange={(e) => setMargem(Number(e.target.value))}
            />
          </div>

          {erroValidacao && (
            <p className="text-sm text-ds-warning">{erroValidacao}</p>
          )}

          <Button
            onClick={handleCalcular}
            disabled={!contexto || isEnqueuing || isRunning || Boolean(erroValidacao)}
            isLoading={isEnqueuing || isRunning}
          >
            {isEnqueuing || isRunning ? 'Calculando precificação...' : 'Calcular precificação'}
          </Button>

          {jobStatus?.status === 'failed' && (
            <div className="flex items-start gap-3 rounded-lg border border-ds-danger/20 bg-ds-danger/10 p-4">
              <AlertTriangle className="h-5 w-5 text-ds-danger shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-ds-danger">Falha ao calcular precificação</p>
                <p className="text-sm text-ds-text-secondary mt-1">{jobStatus.failedReason}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Resultado</CardTitle>
        </CardHeader>
        <CardContent>
          {!contexto ? (
            <EmptyState message="Selecione empresa, licitação e documento para ver a precificação." />
          ) : resultadoLoading ? (
            <p className="text-center text-ds-text-secondary py-8">Carregando...</p>
          ) : !resultado ? (
            <EmptyState message="Nenhuma precificação calculada ainda para esta licitação." />
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { label: 'Custo total', value: resultado.resultado.resumo.custo_total },
                  { label: 'Encargos', value: resultado.resultado.resumo.encargos_total },
                  { label: 'BDI', value: resultado.resultado.resumo.bdi_total },
                  { label: 'Margem', value: resultado.resultado.resumo.margem_total },
                  { label: 'Preço de venda', value: resultado.resultado.resumo.preco_total_venda },
                ].map((card) => (
                  <div key={card.label} className="rounded-lg border border-ds-border bg-ds-surface2 p-4">
                    <p className="text-xs text-ds-text-secondary uppercase tracking-wider">{card.label}</p>
                    <p className="text-lg font-semibold text-ds-text-primary mt-1">{formatCurrency(card.value)}</p>
                  </div>
                ))}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Custo unitário</TableHead>
                    <TableHead>Encargos %</TableHead>
                    <TableHead>BDI %</TableHead>
                    <TableHead>Margem %</TableHead>
                    <TableHead className="text-right">Preço total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resultado.resultado.itens.map((item, index) => (
                    <TableRow key={`${item.codigo}-${index}`}>
                      <TableCell>{item.codigo}</TableCell>
                      <TableCell>{item.descricao}</TableCell>
                      <TableCell>{formatCurrency(item.custo_unitario)}</TableCell>
                      <TableCell>{item.encargos_percentual}%</TableCell>
                      <TableCell>{item.bdi_percentual}%</TableCell>
                      <TableCell>{item.margem_percentual}%</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.preco_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {resultado.resultado.observacoes.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-ds-text-primary mb-2">Observações</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {resultado.resultado.observacoes.map((observacao, index) => (
                      <li key={index} className="text-sm text-ds-text-secondary">{observacao}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export { CentralPlanilhas };
